import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../common/Sidebar";
import "../../styles/StudentDashboard.css";
import api from "../../utils/api";
interface Class {
  id: string;
  course: {
    title: string;
  };
}

interface User {
  id: string;
  username: string;
  role: string;
  school?: string;
}

interface StudentDashboardProps {
  user: User | null;
}

const StudentDashboard: React.FC<StudentDashboardProps> = ({ user }) => {
  const [activeSection, setActiveSection] = useState<"dashboard" | "classes">(
    "dashboard"
  );
  const [activeClasses, setActiveClasses] = useState<Class[]>([]);
  const [classPassword, setClassPassword] = useState("");
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEnterModal, setShowEnterModal] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const [currentSearchInput, setCurrentSearchInput] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [showErrorReportModal, setShowErrorReportModal] = useState(false);
  const [errorReportForm, setErrorReportForm] = useState({
    content: "",
  });
  const [errorReportSuccess, setErrorReportSuccess] = useState<string | null>(
    null
  );
  const [errorReportError, setErrorReportError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // 활성화된 강의 목록 가져오기
  useEffect(() => {
    const fetchActiveClasses = async () => {
      try {
        setIsLoading(true);
        const response = await api.get("/classes");

        // 응답 구조에 따라 데이터 추출
        let classesData;
        if (response.data.result) {
          classesData = response.data.result;
        } else {
          classesData = response.data;
        }

        // 배열 확인
        if (!Array.isArray(classesData)) {
          classesData = [];
        }

        setActiveClasses(classesData);
      } catch (err: any) {
        console.error("Error fetching active classes:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveClasses();
  }, []);

  //   // 참여 가능한 강의 목록 가져오기
  //   useEffect(() => {
  //     const fetchAvailableClasses = async () => {
  //       try {
  //         setIsLoading(true);
  //         const response = await api.get('/classes/available');

  //         // 응답 구조에 따라 데이터 추출
  //         let classesData;
  //         if (response.data.result) {
  //           classesData = response.data.result;
  //         } else {
  //           classesData = response.data;
  //         }

  //         // 배열 확인
  //         if (!Array.isArray(classesData)) {
  //           classesData = [];
  //         }

  //         setAvailableClasses(classesData);
  //       } catch (err: any) {
  //         console.error('Error fetching available classes:', err);
  //       } finally {
  //         setIsLoading(false);
  //       }
  //     };

  //     fetchAvailableClasses();
  //   }, []);

  // 검색 함수 추가
  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    // 현재 입력 값을 실제 검색에 사용할 값으로 설정
    setSearchCode(currentSearchInput);
  };

  // 검색 결과에 맞는 강의 필터링
  const filteredActiveClasses = activeClasses.filter((classItem) =>
    classItem.id.toLowerCase().includes(searchCode.toLowerCase())
  );

  // 강의 입장 모달 열기
  const handleOpenEnterModal = (classId: string) => {
    setSelectedClassId(classId);
    setClassPassword("");
    setError(null);
    setShowEnterModal(true);
    setIsJoining(false);
  };

  // 강의 입장하기
  const handleEnterClass = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClassId || !classPassword) {
      setError("강의 코드와 비밀번호를 모두 입력해주세요.");
      return;
    }

    try {
      setIsJoining(true);
      // API 경로가 정확한지 확인 (슬래시 중복 여부, 경로 등)
      const response = await api.post(`/classes/${selectedClassId}/join`, {
        password: classPassword,
      });

      // 응답 처리
      setShowEnterModal(false);

      // 클래스룸 페이지로 이동
      navigate(`/class/${selectedClassId}`);
    } catch (err: any) {
      console.error("강의 입장 오류:", err);
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || "강의 입장에 실패했습니다.");
      } else {
        setError("서버 연결에 문제가 있습니다.");
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleErrorReportChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setErrorReportForm({
      content: e.target.value,
    });
  };

  const handleSubmitErrorReport = async (e: React.FormEvent) => {
    e.preventDefault();

    if (errorReportForm.content.trim() === "") {
      setErrorReportError("오류 내용을 입력해주세요.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorReportError(null);

      await await api.post("/reports", {
        content: errorReportForm.content,
      });

      setErrorReportSuccess("오류 신고가 접수되었습니다. 감사합니다.");

      // 폼 초기화
      setErrorReportForm({
        content: "",
      });

      // 3초 후 모달 닫기
      setTimeout(() => {
        setShowErrorReportModal(false);
        setErrorReportSuccess(null);
      }, 3000);
    } catch (err) {
      setErrorReportError("오류 신고 제출에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 사이드바 메뉴 아이템
  const sidebarItems = [
    {
      id: "dashboard",
      label: "메인화면",
      icon: "home",
      onClick: () => setActiveSection("dashboard"),
    },
    {
      id: "profile",
      label: "프로필",
      icon: "user",
      onClick: () => navigate("/profile"),
    },
    {
      id: "error-report",
      label: "오류신고",
      icon: "alert",
      onClick: () => setShowErrorReportModal(true),
    },
  ];

  // 모달 열림/닫힘 시 body 스크롤 제어
  useEffect(() => {
    const hasOpenModal = showEnterModal || showErrorReportModal;

    if (hasOpenModal) {
      // 모달이 열릴 때 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("modal-open");
    } else {
      // 모달이 닫힐 때 스크롤 위치 복원
      const scrollY = parseInt(document.body.style.top || "0", 10);
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
      window.scrollTo(0, -scrollY);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
    };
  }, [showEnterModal, showErrorReportModal]);

  return (
    <>
      <Sidebar items={sidebarItems} activeItem={activeSection} user={user} />

      <main className="dashboard-main">
        {activeSection === "dashboard" && (
          <div className="dashboard-overview">
            <h2>학생 대시보드</h2>

            {/* 활성화된 강의 섹션 */}
            <div className="section-container">
              <div className="section-header">
                <div className="section-title">
                  <h3>현재 진행 중인 강의</h3>
                </div>
                <form className="search-container" onSubmit={handleSearch}>
                  <input
                    type="text"
                    placeholder="코드 번호로 검색"
                    value={currentSearchInput}
                    onChange={(e) => setCurrentSearchInput(e.target.value)}
                    className="search-input"
                  />
                  <button
                    className="search-button"
                    aria-label="검색"
                    type="submit"
                  >
                    🔍
                  </button>
                </form>
              </div>

              <div className="class-list">
                {filteredActiveClasses.length > 0 ? (
                  filteredActiveClasses.map((classItem) => (
                    <div key={classItem.id} className="class-item active">
                      <div className="class-info">
                        <div className="active-indicator"></div>
                        <p className="code-label">
                          Code Number: {classItem.id}
                        </p>
                        <h4>{classItem.course.title}</h4>
                      </div>
                      <button
                        className="btn-primary"
                        onClick={() => handleOpenEnterModal(classItem.id)}
                      >
                        입장하기
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="no-classes-message">
                    {searchCode
                      ? "검색 결과가 없습니다."
                      : "현재 진행 중인 강의가 없습니다."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSection === "classes" && (
          <div className="classes-history">
            <h2>강의 기록</h2>
            {/* 강의 기록 내용 */}
          </div>
        )}
      </main>

      {/* 강의 입장 모달 */}
      {showEnterModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>강의 입장하기</h2>
            <form onSubmit={handleEnterClass}>
              <div className="form-group">
                <label htmlFor="enterClassPassword">비밀번호</label>
                <input
                  type="password"
                  id="enterClassPassword"
                  value={classPassword}
                  onChange={(e) => setClassPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  required
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <div className="button-group">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowEnterModal(false)}
                >
                  취소
                </button>
                <button type="submit" className="save-button">
                  입장하기
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 오류 신고 모달 */}
      {showErrorReportModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>오류 신고</h2>
            {errorReportError && (
              <div className="error-message">{errorReportError}</div>
            )}
            {errorReportSuccess && (
              <div className="success-message">{errorReportSuccess}</div>
            )}
            <form onSubmit={handleSubmitErrorReport}>
              <div className="form-group">
                <label htmlFor="errorContent">오류 내용</label>
                <textarea
                  id="errorContent"
                  name="content"
                  value={errorReportForm.content}
                  onChange={handleErrorReportChange}
                  required
                  rows={5}
                  placeholder="발생한 오류에 대해 자세히 설명해주세요."
                />
              </div>
              <div className="button-group">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowErrorReportModal(false)}
                  disabled={isSubmitting}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "처리 중..." : "신고하기"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentDashboard;
